import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { Resizable } from "re-resizable";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getProject } from "@modules/Projects/services/projectCrud";
import { getTreesWithoutProject } from "@modules/Trees/services/treeCrud";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import DataScienceProvider, { DataScienceContext } from "../context/DataScienceContext";
import GraphContainer from "../GraphContainer";
import {
  ReloadIcon,
  FilterIconSolid,
  AngleLeftIcon,
  AngleRightIcon,
  CalendarWeekIcon,
  FullScreenButton,
  FullScreenMinimizeButton,
} from "@components/icons";
import Filter from "../variables/Filter";
import FilterBar from "../filters";
import SeriesChips from "../components/SeriesChips";
import "../DataScience.css";

import Spinner from "@components/Spinner/Spinner";
import { useProjectFilter } from "../../../context/ProjectFilterContext";
import { useBackgroundProjects } from "@hooks/useBackgroundData";

const DS_QUERY_KEYS = [
  "leftVar",
  "installations",
  "areas",
  "trees",
  "variables",
  "rainAreaId",
  "healthTreeId",
  "irrigationTreeId",
  "activeIrrigationData",
];

const getIsCoarsePointer = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return !!window.matchMedia("(pointer: coarse)")?.matches;
  } catch {
    return false;
  }
};

function StandaloneDataScience() {
  const { t } = useTranslation();
  const { mobile } = useSelector((state) => state.mobile);
  const dsTopOffset = "calc(var(--ts-topbar-height) + 0.5rem)";
  const controlsRowHeight = "2.75rem";
  const graphTopOffset = `calc(${dsTopOffset} + ${controlsRowHeight})`;
  const dateSidebarWidth = mobile ? "min(88vw, 320px)" : "220px";
  const [loadData, setLoadData] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [openFilters, setOpenFilters] = useState(true);
  const [openDateFilter, setOpenDateFilter] = useState(!mobile);
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);

  const [isTouchDevice, setIsTouchDevice] = useState(getIsCoarsePointer);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      setIsTouchDevice(false);
      return;
    }
    const mql = window.matchMedia("(pointer: coarse)");
    const handler = (e) => setIsTouchDevice(e.matches);
    setIsTouchDevice(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    if (typeof mql.addListener === "function") {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
    return undefined;
  }, []);
  const isFsEligible = mobile || isTouchDevice;

  const contentLeft = mobile ? "0" : openFilters ? "240px" : "0";
  const dateSidebarSafeGap = "3.5rem";
  const contentRight = mobile
    ? "0"
    : openDateFilter
    ? `calc(${dateSidebarWidth} + ${dateSidebarSafeGap})`
    : "0";

  const isFsActive = isFsEligible && isChartFullscreen;
  const fsGraphTopOffset = isFsActive ? "0" : graphTopOffset;
  const fsBottom = isFsActive ? "0" : "8px";
  const fsContentLeft = isFsActive ? "0" : contentLeft;
  const fsContentRight = isFsActive ? "0" : contentRight;

  const filterSidebarRef = useRef(null);
  const filterToggleRef = useRef(null);
  const filterBarRef = useRef(null);
  const dateSidebarRef = useRef(null);
  const dateToggleRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const { projects, isLoading: isProjectsLoading } = useBackgroundProjects();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const hasProject = Boolean(selectedProjectId);

  useEffect(() => {
    if (isProjectsLoading) return;
    if (selectedProjectId != null) return;
    if (projects?.length === 1) {
      setSelectedProjectId(projects[0].id);
    }
  }, [isProjectsLoading, projects, selectedProjectId, setSelectedProjectId]);

  const isWithoutProject = selectedProjectId === "without-project";

  const { data: allTrees, isLoading: isLoadingTrees } = useQuery(
    "trees-without-project-standalone-ds",
    getTreesWithoutProject,
    {
      enabled: isWithoutProject,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    }
  );

  const pseudoProject = useMemo(() => {
    if (!isWithoutProject || !allTrees) return null;

    const areasMap = {};

    allTrees.forEach((tree) => {
      const areaId = tree.area_id || "without-location";
      const areaName = tree.area_name || t("without_location");

      if (!areasMap[areaId]) {
        areasMap[areaId] = {
          id: areaId,
          name: areaName,
          tree_set: [],
        };
      }

      areasMap[areaId].tree_set.push(tree);
    });

    const areas = Object.values(areasMap);

    return {
      id: "without-project",
      name: t("without_project"),
      areas: areas,
      weather: null,
    };
  }, [isWithoutProject, allTrees, t]);

  const { data: fetchedProject, isLoading: isProjectLoading } = useQuery(
    ["ds_standalone_project", selectedProjectId],
    () => getProject(selectedProjectId),
    {
      enabled: !!selectedProjectId && selectedProjectId !== "without-project",
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const selectedProject = isWithoutProject ? pseudoProject : fetchedProject;

  const [globalOverlayOpen, setGlobalOverlayOpen] = useState(false);

  useEffect(() => {
    const checkNavSidebars = () => {
      const mainMenu = document.querySelector(".main-menu-sidebar.open");
      const projectSidebar = document.querySelector(".project-sidebar-dual.open");
      const projectSelector = document.querySelector(".project-sidebar.open");
      const projectDropdownOpen =
        document.querySelector('#project-filter-dropdown[aria-expanded="true"]') != null;
      const hasOpenNavSidebar = !!(
        mainMenu ||
        projectSidebar ||
        projectSelector ||
        projectDropdownOpen
      );

      setGlobalOverlayOpen(hasOpenNavSidebar);

      if (hasOpenNavSidebar && openFilters) {
        setOpenFilters(false);
      }
    };

    checkNavSidebars();
    const interval = setInterval(checkNavSidebars, 100);
    return () => clearInterval(interval);
  }, [openFilters]);

  useEffect(() => {
    if (!openFilters || !mobile) return;

    const handleClickOutside = (event) => {
      const insideSidebar = filterSidebarRef.current?.contains(event.target);
      const insideFilterBar = filterBarRef.current?.contains(event.target);
      const onToggleButton = filterToggleRef.current?.contains(event.target);

      if (!insideSidebar && !insideFilterBar && !onToggleButton) {
        setOpenFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openFilters, mobile]);

  const handleOpenFilters = useCallback(() => {
    // Close any open navigation sidebar before opening DS filters
    const mainMenuSidebar = document.querySelector(".main-menu-sidebar.open");
    const projectSidebarDual = document.querySelector(".project-sidebar-dual.open");
    const projectSidebar = document.querySelector(".project-sidebar.open");

    const toggleButtons = document.querySelectorAll(".sidebar-toggle-btn");

    if (mainMenuSidebar && toggleButtons[0]) toggleButtons[0].click();
    if (projectSidebarDual && toggleButtons[1]) toggleButtons[1].click();
    if (projectSidebar && toggleButtons[0]) toggleButtons[0].click();

    const projectDropdownToggle = document.querySelector(
      '#project-filter-dropdown[aria-expanded="true"]'
    );
    if (projectDropdownToggle) projectDropdownToggle.click();

    setOpenFilters(true);
  }, []);

  const handleOpenDateFilter = useCallback(() => {
    setOpenDateFilter(true);
  }, []);

  useEffect(() => {
    if (!openDateFilter || !mobile) return;

    const handleClickOutside = (event) => {
      const insideSidebar = dateSidebarRef.current?.contains(event.target);
      const onToggleButton = dateToggleRef.current?.contains(event.target);
      if (!insideSidebar && !onToggleButton) {
        setOpenDateFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDateFilter, mobile]);

  const clearDsParams = (params) => {
    let changed = false;

    DS_QUERY_KEYS.forEach((key) => {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    });

    return changed;
  };

  useEffect(() => {
    if (location.pathname !== "/data-science") return;

    const params = new URLSearchParams(location.search);

    if (!selectedProjectId) {
      const changed = clearDsParams(params);

      if (params.has("projectId")) {
        params.delete("projectId");
      }

      if (changed || location.search.includes("projectId")) {
        const newSearch = params.toString();
        navigate(
          {
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : "",
          },
          { replace: true }
        );
      }

      setShouldRefetch(false);
      setLoadData(false);
      return;
    }

    const urlProjectId = params.get("projectId");

    if (!urlProjectId) {
      params.set("projectId", String(selectedProjectId));
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        { replace: true }
      );
      return;
    }

    if (urlProjectId !== String(selectedProjectId)) {
      const changed = clearDsParams(params);
      params.set("projectId", String(selectedProjectId));

      if (changed || urlProjectId !== String(selectedProjectId)) {
        navigate(
          {
            pathname: location.pathname,
            search: `?${params.toString()}`,
          },
          { replace: true }
        );
      }

      setShouldRefetch(false);
      setLoadData(false);
    }
  }, [selectedProjectId, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!selectedProjectId) {
      setOpenFilters(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    setOpenFilters(true);
    setOpenDateFilter(!mobile);
  }, [mobile, selectedProjectId]);

  useEffect(() => {
    if (!selectedProject) return;

    const q = new URLSearchParams(location.search);
    const hasDsParams =
      !!q.get("areas") && !!q.get("trees") && !!q.get("installations") && !!q.get("leftVar");

    if (hasDsParams) {
      setShouldRefetch(true);
      setLoadData(false);
    }
  }, [selectedProject?.id, location.search]);

  useEffect(() => {
    if (!isFsEligible) return;
    const t1 = setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
    const t2 = setTimeout(() => window.dispatchEvent(new Event("resize")), 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isChartFullscreen, isFsEligible]);

  useEffect(() => {
    if (!isFsEligible) setIsChartFullscreen(false);
  }, [isFsEligible]);

  const headerButtonStyle = {
    background: "var(--bs-primary, #0d6efd)",
    color: "#fff",
    border: "none",
    borderRadius: "0.375rem",
    padding: "0.5rem 0.75rem",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    whiteSpace: "nowrap",
  };

  return (
    <DataScienceProvider key={selectedProjectId || "no-project"}>
      <Breadcrumb />

      <div
        className="ds-top-controls-row"
        style={{
          position: "fixed",
          top: dsTopOffset,
          left: contentLeft,
          right: contentRight,
          minHeight: controlsRowHeight,
          zIndex: 25,
          display: isFsActive ? "none" : "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 0.5rem",
          transition: "left 0.3s ease, right 0.3s ease",
        }}
      >
        <div>
          {selectedProject && !openFilters && (
            <button
              ref={filterToggleRef}
              onClick={handleOpenFilters}
              title={t("filters")}
              style={headerButtonStyle}
              className="ds-top-btn ds-top-btn--left"
            >
              <FilterIconSolid />
              {!mobile && <span>{t("filters")}</span>}
              <AngleRightIcon size={20} />
            </button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <SeriesChips />
          </div>
          {selectedProject && !openDateFilter && (
            <button
              ref={dateToggleRef}
              onClick={handleOpenDateFilter}
              className="ds-top-btn ds-top-btn--right"
              title={t("date")}
              style={headerButtonStyle}
            >
              <AngleLeftIcon size={20} />
              <CalendarWeekIcon />
              {!mobile && <span>{t("date")}</span>}
            </button>
          )}
        </div>
      </div>

      <div
        ref={filterSidebarRef}
        data-tour="datascience-filters"
        className="ds-filter-sidebar"
        style={{
          position: "fixed",
          left: 0,
          top: dsTopOffset,
          bottom: 0,
          width: "240px",
          background: "var(--color-bg-surface, #fff)",
          borderRight: "1px solid var(--color-border-subtle, #dee2e6)",
          paddingLeft: "0.75rem",
          boxSizing: "border-box",
          zIndex: 20,
          overflowY: "auto",
          transition: "transform 0.3s ease",
          transform: openFilters ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        {selectedProject && (
          <div ref={filterBarRef}>
            <FilterBar project={selectedProject} setOpenFilters={setOpenFilters} />
          </div>
        )}
      </div>

      {hasProject && openFilters && !globalOverlayOpen && (
        <button
          className="sidebar-collapse-btn ds-sidebar-collapse-btn-left"
          onClick={() => setOpenFilters(false)}
          title={t("close")}
        >
          <AngleLeftIcon size={20} />
        </button>
      )}

      <Container
        fluid
        className={`ds${isFsActive ? " ds-chart-fullscreen" : ""}`}
        style={{
          position: "fixed",
          top: fsGraphTopOffset,
          bottom: fsBottom,
          left: fsContentLeft,
          right: fsContentRight,
          width: "auto",
          maxWidth: "none",
          transition: "left 0.3s ease, right 0.3s ease, top 0.3s ease, bottom 0.3s ease",
          overflow: "hidden",
          padding: 0,
          zIndex: isFsActive ? 1100 : undefined,
        }}
      >
        {!selectedProjectId ? (
          <div className="ds-empty-state" style={{ height: "100%" }}>
            <p className="ds-empty-state__text">{t("please_select_project")}</p>
          </div>
        ) : isProjectLoading || (isWithoutProject && isLoadingTrees) ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "100%" }}
          >
            <Spinner height={"100%"} />
          </div>
        ) : !selectedProject ? (
          <div className="ds-empty-state" style={{ height: "100%" }}>
            <p className="ds-empty-state__text">{t("project_not_found")}</p>
          </div>
        ) : (
          <Row className="g-0 h-100">
            <Col lg={12} style={{ position: "relative", height: "100%" }}>
              <Row className="g-0 h-100">
                <Col lg={12} className="h-100">
                  <Resizable
                    size={{ width: "100%", height: "100%" }}
                    minHeight={"100%"}
                    maxHeight={"100%"}
                    enable={{
                      top: false,
                      right: false,
                      bottom: false,
                      left: false,
                      topRight: false,
                      bottomRight: false,
                      bottomLeft: false,
                      topLeft: false,
                    }}
                    style={{ position: "relative" }}
                  >
                    <GraphContainer
                      loadData={loadData}
                      setLoadData={setLoadData}
                      setShouldRefetch={setShouldRefetch}
                      shouldRefetch={shouldRefetch}
                    />

                    <div ref={dateSidebarRef}>
                      <Filter
                        openDateFilter={openDateFilter}
                        setOpenDateFilter={setOpenDateFilter}
                        topOffset={dsTopOffset}
                      />
                    </div>

                    {openDateFilter && (
                      <button
                        className="sidebar-collapse-btn sidebar-collapse-btn--right ds-sidebar-collapse-btn-right"
                        onClick={() => setOpenDateFilter(false)}
                        title={t("close_time_controls")}
                        style={{ right: dateSidebarWidth, left: "auto" }}
                      >
                        <AngleRightIcon size={20} />
                      </button>
                    )}

                    {shouldRefetch && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 1000,
                        }}
                      >
                        <Button
                          data-tour="ds-load-data"
                          variant="primary"
                          onClick={() => {
                            setLoadData(true);
                          }}
                        >
                          <ReloadIcon /> {t("load_data")}
                        </Button>
                      </div>
                    )}

                    {isFsEligible && !isChartFullscreen && !openDateFilter && selectedProject && (
                      <button
                        className="ds-fullscreen-toggle"
                        onClick={() => {
                          setOpenFilters(false);
                          setOpenDateFilter(false);
                          setIsChartFullscreen(true);
                        }}
                        title={t("ds_enter_fullscreen")}
                        aria-label={t("ds_enter_fullscreen")}
                      >
                        <FullScreenButton size={18} />
                      </button>
                    )}

                    {isFsActive && (
                      <div className="ds-fullscreen-controls">
                        <button
                          className="ds-fullscreen-control-btn"
                          onClick={() => setIsChartFullscreen(false)}
                          title={t("ds_exit_fullscreen")}
                          aria-label={t("ds_exit_fullscreen")}
                        >
                          <FullScreenMinimizeButton size={18} />
                        </button>
                      </div>
                    )}
                  </Resizable>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Container>
    </DataScienceProvider>
  );
}

export default StandaloneDataScience;
